import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API } from "@/services/API";
import { validateTrailerType } from "@/utils/validation";
import { today } from "@/utils/date";

const initialState = {
  marketToMarketRoute: {
    data: null,
    loading: false,
    error: null,
  },
  ratePrediction: {
    data: null,
    loading: false,
    error: null,
  },
  modelRatePrediction: {
    data: null,
    loading: false,
    error: null,
  },
};

export const getRatePrediction = createAsyncThunk(
  "search/get-rate-prediction",
  async ({ trailerType, pickupZip, deliveryZip, pickupDate }, { rejectWithValue }) => {
    if (!trailerType || !pickupZip || !deliveryZip || !validateTrailerType(trailerType) || isNaN(pickupZip) || isNaN(deliveryZip)) {
      return rejectWithValue("Invalid rate prediction parameters provided");
    }

    return API.get("/analytics/load-price-and-trend", {
      params: {
        departure: pickupZip,
        "drop-off": deliveryZip,
        trailer: trailerType,
        "for-date": pickupDate || today,
        "use-other-sources": true,
      },
    })
      .then((res) => res)
      .catch((err) => {
        console.error("Error fetching rate prediction data", err);
        return rejectWithValue(err);
      });
  },
);

export const getRateWithModel = createAsyncThunk(
  "search/get-rate-model",
  async ({ trailerType, pickupZip, deliveryZip, pickupDate }, { rejectWithValue }) => {
    if (!trailerType || !pickupZip || !deliveryZip || !validateTrailerType(trailerType) || isNaN(pickupZip) || isNaN(deliveryZip)) {
      return rejectWithValue("Invalid model rate prediction parameters provided");
    }

    return API.get("/analytics/load-prices-with-model", {
      params: {
        departure: pickupZip,
        "drop-off": deliveryZip,
        trailer: trailerType,
        "for-date": pickupDate || today,
        "dead-head-miles-departure": 5000,
        "radius-miles-drop-off": 5000,
        "time-of-day": "full_day",
      },
    })
      .then((res) => res)
      .catch((err) => {
        console.error("Error fetching model rate prediction data", err);
        return rejectWithValue(err);
      });
  },
);

export const getMarketToMarketRoute = createAsyncThunk(
  "search/get-market-to-market-route",
  async ({ pickupZip, deliveryZip }, { rejectWithValue }) => {
    if (!pickupZip || !deliveryZip || isNaN(pickupZip) || isNaN(deliveryZip)) {
      return rejectWithValue("Invalid market to market route parameters provided");
    }

    return API.get("/marketinfo/market2market-route", {
      params: {
        departure: pickupZip,
        "drop-off": deliveryZip,
      },
    })
      .then((res) => res)
      .catch((err) => {
        console.error("Error fetching market to market route data", err);
        return rejectWithValue(err);
      });
  },
);

const loadRatesSlice = createSlice({
  name: "loadRates",
  initialState,
  reducers: {
    clearLoadRatesState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRatePrediction.pending, (state) => {
        state.ratePrediction = {
          data: null,
          loading: true,
          error: null,
        };
      })
      .addCase(getRatePrediction.fulfilled, (state, action) => {
        state.ratePrediction = {
          data: action.payload,
          loading: false,
          error: null,
        };
      })
      .addCase(getRatePrediction.rejected, (state) => {
        state.ratePrediction = {
          data: null,
          loading: false,
          error: "Error getting rate prediction",
        };
      })
      .addCase(getRateWithModel.pending, (state) => {
        state.modelRatePrediction = {
          data: null,
          loading: true,
          error: null,
        };
      })
      .addCase(getRateWithModel.fulfilled, (state, action) => {
        state.modelRatePrediction = {
          data: action.payload,
          loading: false,
          error: null,
        };
      })
      .addCase(getRateWithModel.rejected, (state) => {
        state.modelRatePrediction = {
          data: null,
          loading: false,
          error: "Error getting model rate prediction",
        };
      })
      .addCase(getMarketToMarketRoute.pending, (state) => {
        state.marketToMarketRoute = {
          data: null,
          loading: true,
          error: null,
        };
      })
      .addCase(getMarketToMarketRoute.fulfilled, (state, action) => {
        state.marketToMarketRoute = {
          data: action.payload,
          loading: false,
          error: null,
        };
      })
      .addCase(getMarketToMarketRoute.rejected, (state) => {
        state.marketToMarketRoute = {
          data: null,
          loading: false,
          error: "Error getting market to market route data",
        };
      });
  },
});

export const { clearLoadRatesState } = loadRatesSlice.actions;
export default loadRatesSlice.reducer;
